/**
 * FUNNEL SIGNALS · field completions and the four drop-off CAUSES.
 *
 * Arrivals say WHERE people leave. These say WHY: a generation that failed, a
 * payment that declined, a bank check abandoned, a code requested and never
 * typed, and a step someone walked back out of. Without them a cliff in the
 * table names a screen and nothing else.
 *
 * The allowed keys live in `funnel_states` (one authority); this module only
 * decides WHEN each one is recorded.
 *
 * The shared back affordance and the payment element are both far from the
 * program context, so the current slug/program is parked here by the flow shell
 * rather than threaded through every component as props. A signal with no
 * context is DROPPED, never guessed — a mis-attributed row is worse than a
 * missing one.
 */
import { recordFunnelEvent, type FunnelState } from "@/lib/consumer-session";

let ctx: { slug: string; programId: string | null } | null = null;

export function setFunnelContext(slug: string, programId: string | null) {
  ctx = { slug, programId };
}

export function clearFunnelContext() {
  ctx = null;
}

/** Records a signal for the current screen context. No context = no row. */
export function recordSignal(state: FunnelState, meta?: Record<string, unknown>) {
  if (!ctx) return;
  recordFunnelEvent(ctx.slug, state, ctx.programId, meta);
}

const once = new Set<string>();

/**
 * Once per visit. Field completion is a threshold crossing, not a keystroke —
 * re-editing a valid field must not add a second row.
 */
export function recordSignalOnce(state: FunnelState, meta?: Record<string, unknown>) {
  const key = `${ctx?.slug ?? "-"}:${state}`;
  if (once.has(key)) return;
  once.add(key);
  recordSignal(state, meta);
}

/** Backward navigation FROM a step, recorded by the one back affordance. */
export function recordSteppedBack(fromPath: string, to?: string) {
  const from = fromPath.replace(/\/+$/, "").split("/").pop() || fromPath;
  recordSignal("stepped_back", { from, to: to ?? null });
}

/** Test seam only. */
export function __resetSignals() {
  once.clear();
  ctx = null;
}
